import React, { useEffect, useState, useMemo } from "react";
import { generateCalibrationPDF } from "../../utils/GeneratePDFReport";
import ComboFiltro from "../../components/utils/ComboFiltro";
import ChartRepetitibilidadPro from "../utils/ChartRepetitibilidadPro";
import ChartPromedioErrorVolumen from "../utils/ChartPromedioErrorVolumen";
import ChartCargaErrorPorVolumen from "../utils/ChartCargaErrorPorVolumen";
import ChartDistribucionErrores from "../utils/ChartDistribucionErrores";


import "../../styles/frmCalibracion.css"
import { useCalibracion } from '../../context/FormsProyect/CallibracionContext'
import GlobalTable from "../utils/GlobalTable";
import axios from "axios";
import getParamsEnv from "../../utils/getParamsEnv";

const FrmCalibracion = () => {
    const {
        filtros: contextFiltros,
        setFiltros: setContextFiltros,
        handleFiltroChange: handleContextFiltroChange,
        handleobtenerDatosGraficas,
        datosGraficas,
        saveCalibracionDocument
    } = useCalibracion();

    // Estado local para los Filtros Visuales (Combos)
    // El 'contextFiltros' se usará principalmente para las Fechas en la consulta a API.
    // Estos 'localFiltros' filtran la data YA cargada en memoria.
    const [localFiltros, setLocalFiltros] = useState({
        compania: "",
        planta: "",
        mesacarga: "",
        brazo: "",
        producto: "",
        tipoFlujo: "",
        clasificacion: "",
        categoria: "", // AuxCalibracion
    });

    // Loading state for PDF
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

    // Estado para colapsar secciones
    const [showQuery, setShowQuery] = useState(true);
    const [showFilters, setShowFilters] = useState(true);

    const [activeTab, setActiveTab] = useState("graficos"); // 'graficos', 'repetibilidad', 'general'
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageGeneral, setCurrentPageGeneral] = useState(1);
    const itemsPerPage = 10;

    // Initial Date Logic...
    useEffect(() => {
        if (!contextFiltros.fechaInicio || !contextFiltros.fechaFin) {
            const today = new Date();
            const fechaInicio = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().substring(0, 10);
            const fechaFin = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().substring(0, 10);

            setContextFiltros(prev => ({ ...prev, fechaInicio, fechaFin }));

            // Cargar datos iniciales
            handleobtenerDatosGraficas({
                fechaInicio,
                fechaFin,
                compania: "",
                planta: "",
                mesacarga: "",
                brazo: "",
                producto: "",
                tipoFlujo: ""
            });
        }
    }, []);

    // --- LÓGICA DE CONSULTA (DATA FETCHING) ---
    // Consulta a la API trayendo TODO el universo de datos para las fechas seleccionadas.
    // Ignoramos los filtros de combo en la petición para permitir filtrado dinámico local.
    const handleConsultar = () => {
        handleobtenerDatosGraficas({
            fechaInicio: contextFiltros.fechaInicio,
            fechaFin: contextFiltros.fechaFin,
            // Enviamos vacíos los demás campos para que traiga "Todo lo permitido"
            compania: "",
            planta: "",
            mesacarga: "",
            brazo: "",
            producto: "",
            tipoFlujo: ""
        });
        setCurrentPage(1);
    };

    // --- LÓGICA DE FILTRADO DEPENDIENTE (DYNAMIC LISTS & DATA) ---

    // 1. Obtener la data cruda desde el contexto
    const rawData = datosGraficas?.limitesRaw || []; // Detalle General
    const rawRepetibilidad = datosGraficas?.repetibilidadTableData || []; // Detalle Repetibilidad

    // 2. Filtrar la data según `localFiltros`
    const { filteredGeneral, filteredRepetibilidad } = useMemo(() => {
        const check = (item, field, filterValue) => !filterValue || item[field] === filterValue;

        const filterFn = (item) => {
            // Access nested props safely
            const itemClasificacion = item.Clasificacion || item.DocumentoCalibracion?.Clasificacion;
            const itemCategoria = item.AuxCalibracion || item.DocumentoCalibracion?.AuxCalibracion;

            return (
                check(item, 'Compania', localFiltros.compania) &&
                check(item, 'Planta', localFiltros.planta) &&
                check(item, 'MesaCarga', localFiltros.mesacarga) &&
                check(item, 'Brazo', localFiltros.brazo) &&
                check(item, 'Producto', localFiltros.producto) &&
                check(item, 'Tipo_Flujo', localFiltros.tipoFlujo) &&
                (!localFiltros.clasificacion || itemClasificacion === localFiltros.clasificacion) &&
                (!localFiltros.categoria || itemCategoria === localFiltros.categoria)
            );
        };

        return {
            filteredGeneral: rawData.filter(filterFn),
            filteredRepetibilidad: rawRepetibilidad.filter(filterFn)
        };
    }, [rawData, rawRepetibilidad, localFiltros]);

    // 3. Generar Opciones Dinámicas para los Combos (Cascada)
    // Se basan en la data FILTRADA parciamente.
    // Ejemplo: Si selecciono Compañía, las opciones de Plantas deben ser las disponibles para ESA compañía.
    // Pero si selecciono Planta, las opciones de Compañía deben mantenerse (o filtrarse inversamente? UX normal es drill-down).

    // Helper para obtener unicos
    const getUniqueOptions = (sourceData, field) => {
        const unique = [...new Set(sourceData.map(item => item[field]).filter(Boolean))];
        return unique.sort().map(val => ({ label: val, value: val }));
    };

    // Usamos 'rawData' como base, pero aplicamos los filtros "aguas arriba".
    // Compañias: Siempre todas las disponibles en el rango de fechas.
    const opcionesCompanias = useMemo(() => getUniqueOptions(rawData, 'Compania'), [rawData]);

    // Plantas: Filtradas por compañia seleccionada
    const plantasSource = localFiltros.compania ? rawData.filter(d => d.Compania === localFiltros.compania) : rawData;
    const opcionesPlantas = useMemo(() => getUniqueOptions(plantasSource, 'Planta'), [plantasSource]);

    // Mesas: Filtradas por Compania Y Planta
    const mesasSource = localFiltros.planta ? plantasSource.filter(d => d.Planta === localFiltros.planta) : plantasSource;
    const opcionesMesasCarga = useMemo(() => getUniqueOptions(mesasSource, 'MesaCarga'), [mesasSource]);

    // Brazos: Filtradas por ... Y Mesa
    const brazosSource = localFiltros.mesacarga ? mesasSource.filter(d => d.MesaCarga === localFiltros.mesacarga) : mesasSource;
    const opcionesBrazos = useMemo(() => getUniqueOptions(brazosSource, 'Brazo'), [brazosSource]);

    // Productos: Filtrados por ... Y Brazo
    const productosSource = localFiltros.brazo ? brazosSource.filter(d => d.Brazo === localFiltros.brazo) : brazosSource;
    const opcionesProductos = useMemo(() => getUniqueOptions(productosSource, 'Producto'), [productosSource]);

    // Tipos Flujo: Filtrados por ... Y Producto
    const tiposFlujoSource = localFiltros.producto ? productosSource.filter(d => d.Producto === localFiltros.producto) : productosSource;
    const opcionesTiposFlujo = useMemo(() => getUniqueOptions(tiposFlujoSource, 'Tipo_Flujo'), [tiposFlujoSource]);

    // Clasificacion: Filtrados por ... Y TipoFlujo
    const clasificacionSource = localFiltros.tipoFlujo ? tiposFlujoSource.filter(d => d.Tipo_Flujo === localFiltros.tipoFlujo) : tiposFlujoSource;
    const opcionesClasificacion = useMemo(() => {
        const values = clasificacionSource.map(item => item.Clasificacion || item.DocumentoCalibracion?.Clasificacion).filter(Boolean);
        const unique = [...new Set(values)];
        return unique.sort().map(val => ({ label: val, value: val }));
    }, [clasificacionSource]);

    // Categoria (AuxCalibracion): Filtrados por ... Y Clasificacion
    const categoriaSource = localFiltros.clasificacion ?
        clasificacionSource.filter(d => (d.Clasificacion || d.DocumentoCalibracion?.Clasificacion) === localFiltros.clasificacion)
        : clasificacionSource;

    const opcionesCategoria = useMemo(() => {
        const values = categoriaSource.map(item => item.AuxCalibracion || item.DocumentoCalibracion?.AuxCalibracion).filter(Boolean);
        const unique = [...new Set(values)];
        return unique.sort().map(val => ({ label: val, value: val }));
    }, [categoriaSource]);


    // Manejador de cambios en localFiltros con limpieza en cascada
    const handleLocalChange = (e) => {
        const { name, value } = e.target;
        setLocalFiltros(prev => {
            const newState = { ...prev, [name]: value };

            // Limpieza en cascada
            if (name === "compania") {
                newState.planta = "";
                newState.mesacarga = "";
                newState.brazo = "";
                newState.producto = "";
                newState.tipoFlujo = "";
                newState.clasificacion = "";
                newState.categoria = "";
            }
            if (name === "planta") {
                newState.mesacarga = "";
                newState.brazo = "";
                newState.producto = "";
                newState.tipoFlujo = "";
                newState.clasificacion = "";
                newState.categoria = "";
            }
            if (name === "mesacarga") {
                newState.brazo = "";
                newState.producto = "";
                newState.tipoFlujo = "";
                newState.clasificacion = "";
                newState.categoria = "";
            }
            if (name === "brazo") {
                newState.producto = "";
                newState.tipoFlujo = "";
                newState.clasificacion = "";
                newState.categoria = "";
            }
            if (name === "producto") {
                newState.tipoFlujo = "";
                newState.clasificacion = "";
                newState.categoria = "";
            }
            if (name === "tipoFlujo") {
                newState.clasificacion = "";
                newState.categoria = "";
            }
            if (name === "clasificacion") {
                newState.categoria = "";
            }

            return newState;
        });
        setCurrentPage(1);
    };


    // Limpiar Filtros Locales
    const handleClearLocalFilters = () => {
        setLocalFiltros({
            compania: "",
            planta: "",
            mesacarga: "",
            brazo: "",
            producto: "",
            tipoFlujo: "",
            clasificacion: "",
            categoria: "",
        });
        setCurrentPage(1);
    };

    // --- CALCULO DE GRÁFICOS CLIENT-SIDE ---
    // Recalcular promedios basados en 'filteredRepetibilidad'
    const chartDataRepetibilidad = useMemo(() => {
        if (filteredRepetibilidad.length === 0 && filteredGeneral.length === 0) return {};

        const chartData = {};

        // 1. Agrupar KPI L1 desde filteredRepetibilidad
        filteredRepetibilidad.forEach(item => {
            const d = new Date(item.Fecha);
            const k = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

            if (!chartData[k]) chartData[k] = { kpiSum: 0, count: 0, ls1Sum: 0, li1Sum: 0, limitsCount: 0 };

            if (item.KpiL1 != null) {
                chartData[k].kpiSum += parseFloat(item.KpiL1);
                chartData[k].count++;
            }
        });

        // 2. Agrupar LS1/LI1 desde filteredGeneral (limitesRaw)
        // Nota: Fechas deben matchear.
        filteredGeneral.forEach(item => {
            const d = new Date(item.Fecha);
            const k = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

            if (chartData[k]) {
                // Si existe la fecha en KPI, agregamos datos de limites
                if (item.LS1 != null && item.LI1 != null) {
                    chartData[k].ls1Sum += parseFloat(item.LS1);
                    chartData[k].li1Sum += parseFloat(item.LI1);
                    chartData[k].limitsCount++;
                }
            }
        });

        const finalChartData = {};
        Object.keys(chartData).forEach(date => {
            const d = chartData[date];
            finalChartData[date] = [
                { serie: "KPI L1", valor: d.count ? d.kpiSum / d.count : 0 },
                { serie: "LS1", valor: d.limitsCount ? d.ls1Sum / d.limitsCount : 0 },
                { serie: "LI1", valor: d.limitsCount ? d.li1Sum / d.limitsCount : 0 },
            ];
        });

        return finalChartData;

    }, [filteredRepetibilidad, filteredGeneral]);

    // 2. Calculo CLIENT-SIDE para 'Promedio Error Volumétrico' usando filteredGeneral
    const chartPromedioErrorVolumen = useMemo(() => {
        if (!filteredGeneral || filteredGeneral.length === 0) return {};

        const grouped = {};
        filteredGeneral.forEach(item => {
            const d = new Date(item.Fecha);
            const k = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

            if (!grouped[k]) grouped[k] = { sum: 0, count: 0 };

            // Usamos ErrorVolumenBruto como en el backend
            const val = parseFloat(item.ErrorVolumenBruto);
            if (!isNaN(val)) {
                grouped[k].sum += val;
                grouped[k].count++;
            }
        });

        const result = {};
        Object.keys(grouped).forEach(date => {
            const avg = grouped[date].count > 0 ? grouped[date].sum / grouped[date].count : 0;
            result[date] = [{
                serie: "Promedio error volumen bruto",
                valor: avg
            }];
        });
        return result;
    }, [filteredGeneral]);


    // 3. Calculo CLIENT-SIDE para 'Variación por Carga' usando filteredGeneral
    const chartValoresPorCarga = useMemo(() => {
        if (!filteredGeneral || filteredGeneral.length === 0) return {};

        const groupedByDateAndCarga = {};

        filteredGeneral.forEach(item => {
            const d = new Date(item.Fecha);
            const dateKey = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
            const cargaKey = item.carga ? item.carga.toString() : "0";

            if (!groupedByDateAndCarga[dateKey]) groupedByDateAndCarga[dateKey] = {};
            if (!groupedByDateAndCarga[dateKey][cargaKey]) {
                groupedByDateAndCarga[dateKey][cargaKey] = { sumBruto: 0, sumNeto: 0, count: 0 };
            }

            const bruto = parseFloat(item.ErrorVolumenBruto);
            const neto = parseFloat(item.ErrorVolumenNeto);

            if (!isNaN(bruto)) groupedByDateAndCarga[dateKey][cargaKey].sumBruto += bruto;
            if (!isNaN(neto)) groupedByDateAndCarga[dateKey][cargaKey].sumNeto += neto;
            groupedByDateAndCarga[dateKey][cargaKey].count++;
        });

        const result = {};
        Object.keys(groupedByDateAndCarga).forEach(date => {
            result[date] = Object.keys(groupedByDateAndCarga[date]).map(carga => {
                const data = groupedByDateAndCarga[date][carga];
                return {
                    serie: carga,
                    valor1: data.count > 0 ? data.sumBruto / data.count : 0,
                    valor2: data.count > 0 ? data.sumNeto / data.count : 0
                };
            });
        });

        return result;
    }, [filteredGeneral]);

    // PAGINACION REPETIBILIDAD
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentTableData = filteredRepetibilidad.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredRepetibilidad.length / itemsPerPage);
    const paginate = (pageNumber) => setCurrentPage(pageNumber);


    // Generar PDF
    const handleExportPDF = async () => {
        const prevTab = activeTab;
        let tabSwitched = false;

        if (activeTab !== 'graficos') {
            setActiveTab('graficos');
            tabSwitched = true;
        }

        // If we capture too soon, bars are at 0 height.
        // We can disable animation in recharts, or wait. 
        // 800ms is usually enough for data animation and is faster.
        const waitTime = tabSwitched ? 1000 : 300;

        setIsGeneratingPDF(true);
        // document.body.style.cursor = 'wait'; // Handled by modal now

        // Fetch Empresa Data while waiting?
        // We can do it before setting timeout to save time
        let empresaData = {};
        try {
            const { API_URL_BASE } = getParamsEnv();
            const token = localStorage.getItem("token");
            if (token) {
                const res = await axios.get(`${API_URL_BASE}empresa`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { nombre: localFiltros.compania || contextFiltros.compania }
                });
                if (res.status === 200) {
                    empresaData = res.data;
                }
            }
        } catch (e) {
            console.warn("Could not fetch company info for report", e);
        }

        setTimeout(async () => {
            try {
                await generateCalibrationPDF({
                    filtros: { ...contextFiltros, ...localFiltros },
                    chartIds: ['chart-repetibilidad', 'chart-distribucion', 'chart-error-volumen', 'chart-carga'],
                    tableDataRepetibilidad: filteredRepetibilidad,
                    tableDataGeneral: filteredGeneral,
                    dataPromedioErrorVolumen: chartPromedioErrorVolumen,
                    dataValoresPorCarga: chartValoresPorCarga,
                    empresa: empresaData
                });
            } catch (error) {
                console.error("Error PDF:", error);
                alert("Error generando el PDF. Intente nuevamente.");
            } finally {
                setIsGeneratingPDF(false);
                if (tabSwitched) setActiveTab(prevTab);
            }
        }, waitTime);
    };



    // RENDER
    return (
        <div className="calibracion-container">
            {/* NO TITLE HERE as requested */}

            {/* SECCION 1: CONSULTA (FECHAS) - COLLAPSIBLE */}
            <div className="collapsible-section compact-section">
                <div className="collapsible-header compact-header" onClick={() => setShowQuery(!showQuery)}>
                    <div className="collapsible-title">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        Rango de Consulta
                    </div>
                    {showQuery ?
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg> :
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    }
                </div>

                {showQuery && (
                    <div className="collapsible-content query-content compact-content">
                        <div className="form-group" style={{ flex: 1, minWidth: '150px' }}>
                            <label>Fecha Inicio</label>
                            <input type="date" name="fechaInicio" value={contextFiltros.fechaInicio} onChange={handleContextFiltroChange} />
                        </div>
                        <div className="form-group" style={{ flex: 1, minWidth: '150px' }}>
                            <label>Fecha Fin</label>
                            <input type="date" name="fechaFin" value={contextFiltros.fechaFin} onChange={handleContextFiltroChange} />
                        </div>
                        <button className="btn-consultar" onClick={handleConsultar}>
                            CONSULTAR
                        </button>
                    </div>
                )}
            </div>

            {/* SECCION 2: FILTROS DINAMICOS - COLLAPSIBLE */}
            <div className="collapsible-section compact-section">
                <div className="collapsible-header compact-header" onClick={() => setShowFilters(!showFilters)}>
                    <div className="collapsible-title">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
                        Filtros Dinámicos
                        {/* PDF Button */}
                        <button
                            className="ml-auto mr-2 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                            onClick={(e) => { e.stopPropagation(); handleExportPDF(); }}
                            title="Exportar Reporte Ejecutivo"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                        </button>

                        {/* Clear Filters Button */}
                        <button
                            className="p-1 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded transition-colors"
                            onClick={(e) => { e.stopPropagation(); handleClearLocalFilters(); }}
                            title="Limpiar todos los filtros"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                    </div>
                    {/* Clear Button Header Quick Access if collapsed? No, inside. */}
                    {showFilters ?
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg> :
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    }
                </div>

                {showFilters && (
                    <div className="collapsible-content compact-content">
                        <div className="filtros-grid">
                            <ComboFiltro label="Compañía" name="compania" value={localFiltros.compania} onChange={handleLocalChange} options={opcionesCompanias} />
                            <ComboFiltro label="Planta" name="planta" value={localFiltros.planta} onChange={handleLocalChange} options={opcionesPlantas} />
                            <ComboFiltro label="Mesa" name="mesacarga" value={localFiltros.mesacarga} onChange={handleLocalChange} options={opcionesMesasCarga} />
                            <ComboFiltro label="Brazo" name="brazo" value={localFiltros.brazo} onChange={handleLocalChange} options={opcionesBrazos} />
                            <ComboFiltro label="Producto" name="producto" value={localFiltros.producto} onChange={handleLocalChange} options={opcionesProductos} />
                            <ComboFiltro label="Tipo Flujo" name="tipoFlujo" value={localFiltros.tipoFlujo} onChange={handleLocalChange} options={opcionesTiposFlujo} />
                            <ComboFiltro label="Clasificación" name="clasificacion" value={localFiltros.clasificacion} onChange={handleLocalChange} options={opcionesClasificacion} />
                            <ComboFiltro label="Categoría" name="categoria" value={localFiltros.categoria} onChange={handleLocalChange} options={opcionesCategoria} />

                            {/* Clear Button in grid */}

                        </div>
                    </div>
                )}
            </div>

            {/* TABS DE VISTA */}
            <div className="tabs-container">
                <nav className="flex space-x-8" aria-label="Tabs">
                    {['graficos', 'repetibilidad', 'general'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`
py - 4 px - 1 border - b - 2 font - medium text - sm transition - colors
                                ${activeTab === tab
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }
`}
                        >
                            {tab === 'graficos' && "Gráficos KPI"}
                            {tab === 'repetibilidad' && "Tabla Repetibilidad (L1)"}
                            {tab === 'general' && "Detalles Generales"}
                        </button>
                    ))}
                </nav>
            </div>

            <div className="p-4 bg-white border border-t-0 border-gray-200 rounded-b-lg mb-8 shadow-sm">

                {/* 1. GRAFICOS */}
                {activeTab === 'graficos' && (
                    <div className="graficos-container animated fadeIn">
                        <div className="graficos-grid">
                            <div id="chart-repetibilidad"><ChartRepetitibilidadPro data={chartDataRepetibilidad} /></div>
                            <div id="chart-distribucion"><ChartDistribucionErrores data={filteredGeneral} /></div>
                            <div id="chart-error-volumen"><ChartPromedioErrorVolumen promedioErrorVolumen={chartPromedioErrorVolumen} /></div>
                            <div id="chart-carga"><ChartCargaErrorPorVolumen promedioCargaErrorVolumen={chartValoresPorCarga} /></div>
                        </div>
                    </div>
                )}


                {/* 2. TABLA REPETIBILIDAD */}
                {activeTab === 'repetibilidad' && (
                    <div className="table-wrapper animated fadeIn">
                        <GlobalTable
                            storageKey="calib_repetibilidad_l1"
                            title="Detalle de Repetibilidad (KPI L1)"
                            data={filteredRepetibilidad}
                            columns={[
                                { header: "Compañía", accessor: "Compania" },
                                { header: "Planta", accessor: "Planta" },
                                { header: "Mesa", accessor: "MesaCarga" },
                                { header: "Brazo", accessor: "Brazo" },
                                { header: "Producto", accessor: "Producto" },
                                { header: "Fecha", accessor: "Fecha", render: (row) => new Date(row.Fecha).toLocaleDateString() },
                                { header: "KPI L1", accessor: "KpiL1", render: (row) => row.KpiL1 != null ? <span className="font-mono text-[#005f88] font-bold">{Number(row.KpiL1).toFixed(4)}</span> : "-" },
                                {
                                    header: "Estado S.", accessor: "EstadoSecuenciaL1", render: (row) => (
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${row.EstadoSecuenciaL1 === 'OK' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'bg-red-100 text-red-800 border border-red-200'}`}>
                                            {row.EstadoSecuenciaL1}
                                        </span>
                                    )
                                },
                                { header: "Secuencia L1", accessor: "ValoresSecuencia3Consecutivos_L1" },
                                { header: "Cargas", accessor: "CargasConDatos" }
                            ]}
                        />
                    </div>
                )}

                {/* 3. TABLA GENERAL */}
                {activeTab === 'general' && (
                    <div className="table-wrapper animated fadeIn">
                        <GlobalTable
                            storageKey="calib_detalle_general"
                            title="Resultados de Calibración General"
                            data={filteredGeneral}
                            columns={[
                                { header: "Fecha", accessor: "FechaConvert" },
                                { header: "Brazo", accessor: "Brazo" },
                                { header: "Producto", accessor: "Producto" },
                                { header: "Carga", accessor: "carga", render: (r) => <span className="font-bold text-[#005f88]">#{r.carga}</span> },
                                { header: "Error Vol. Bruto", accessor: "ErrorVolumenBruto", render: (r) => <div className="text-right font-mono">{r.ErrorVolumenBruto}</div> },
                                { header: "Error Vol. Neto", accessor: "ErrorVolumenNeto", render: (r) => <div className="text-right font-mono font-bold text-slate-800">{r.ErrorVolumenNeto}</div> },
                                { header: "Dif. Vol. Bruto", accessor: "DifVolBruto", render: (r) => <div className="text-right font-mono">{r.DifVolBruto}</div> },
                                { header: "Dif. Vol. Neto", accessor: "DifVolNeto", render: (r) => <div className="text-right font-mono">{r.DifVolNeto}</div> },
                                { header: "Tipo Flujo", accessor: "Tipo_Flujo" },
                            ]}
                        />
                    </div>
                )}
            </div>

            {/* GENERATING PDF MODAL */}
            {isGeneratingPDF && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] backdrop-blur-sm">
                    <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center animate-bounce-in border border-gray-100">
                        <div className="relative mb-4">
                            <div className="absolute inset-0 bg-blue-100 rounded-full animate-ping opacity-75"></div>
                            <svg className="relative animate-spin h-12 w-12 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">Generando Reporte</h3>
                        <p className="text-sm text-gray-500">Procesando gráficos y datos...</p>
                    </div>
                </div>
            )}
        </div>
    );
};
export default FrmCalibracion;
